import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getMyTenants, getMyPermissions } from '@/services/admin';

const TenantContext = createContext(null);

export const useTenant = () => {
  const context = useContext(TenantContext);
  if (!context) {
    // Return default values when context is not available (during initial load)
    return {
      loading: true,
      error: null,
      isSuperAdmin: false,
      tenants: [],
      currentTenant: null,
      permissions: [],
      userRole: null,
      switchTenant: () => {},
      refresh: () => {},
      hasPermission: () => false,
      hasAnyPermission: () => false,
      isModuleEnabled: () => true, // Allow all by default during loading
      canAccessModule: () => true,
      isTenantAdmin: () => false
    };
  }
  return context;
};

export const TenantProvider = ({ children }) => {
  const [loading, setLoading] = useState(true);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [tenants, setTenants] = useState([]);
  const [currentTenant, setCurrentTenant] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [userRole, setUserRole] = useState(null);

  /**
   * When the per-tenant role escalates (e.g. the user is a tenant ``owner``
   * even though their global ``users.role`` is still ``auditor``), reflect
   * that in the cached ``user`` object in ``localStorage``. Callers that
   * read ``JSON.parse(localStorage.user).role`` — the sidebar RBAC guards,
   * SettingsPage's ``canManageUsers`` check, etc. — see the elevated role
   * without a fresh login.
   *
   *   tenantRole 'owner' -> localStorage.user.role = 'admin'
   *   tenantRole 'admin' -> localStorage.user.role = 'admin'
   *   tenantRole 'viewer'/'editor'/'auditor' -> restore the original global role
   *
   * The original global role is remembered under
   * ``localStorage.user._originalRole`` so we can restore it whenever the
   * user switches to a tenant where they are *not* an owner/admin.
   */
  const syncLocalUserRole = useCallback((tenantRole) => {
    try {
      const raw = localStorage.getItem('user');
      if (!raw) return;
      const u = JSON.parse(raw);
      const original = u._originalRole || u.role;
      let next = original;
      if (['owner', 'admin', 'tenant_admin'].includes(tenantRole)) {
        next = 'admin';
      }
      if (u.role === next && u._originalRole === original) return;
      const updated = { ...u, role: next, _originalRole: original };
      localStorage.setItem('user', JSON.stringify(updated));
      // Notify same-tab listeners (the ``storage`` event only fires on
      // *other* tabs) so pages re-render immediately.
      window.dispatchEvent(new CustomEvent('local-user-role-changed', {
        detail: { role: next, tenantRole },
      }));
    } catch (e) {
      // localStorage might be JSON-corrupt on very old sessions — ignore.
    }
  }, []);
  const [error, setError] = useState(null);

  // Load user's tenants and permissions
  const loadUserContext = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const data = await getMyTenants();
      setIsSuperAdmin(data.is_super_admin === true);
      setTenants(data.tenants || []);
      
      // Auto-select first tenant if none selected
      if (data.tenants?.length > 0 && !currentTenant) {
        // Check if there's a stored tenant preference
        const storedTenantId = localStorage.getItem('currentTenantId');
        const defaultTenant = storedTenantId 
          ? data.tenants.find(t => t.id === storedTenantId) || data.tenants[0]
          : data.tenants[0];
        // Store in localStorage for API requests (before state so refetches use it)
        localStorage.setItem('currentTenantId', defaultTenant.id);
        setCurrentTenant(defaultTenant);
        
        // Load permissions for the default tenant
        if (defaultTenant.id) {
          const perms = await getMyPermissions(defaultTenant.id);
          setPermissions(perms.permissions || []);
          setUserRole(perms.role);
          syncLocalUserRole(perms.role);
        }
      }
    } catch (err) {
      console.error('[TenantContext] Failed to load tenant context:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [currentTenant, syncLocalUserRole]);

  // Load context on mount and when token changes
  useEffect(() => {
    loadUserContext();
    
    // Listen for storage changes (login/logout from other tabs)
    const handleStorageChange = (e) => {
      if (e.key === 'token') {
        loadUserContext();
      }
    };
    
    // Listen for custom login event (from same tab)
    const handleLoginEvent = () => {
      setTimeout(() => loadUserContext(), 100); // Small delay to ensure token is saved
    };
    
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('user-logged-in', handleLoginEvent);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('user-logged-in', handleLoginEvent);
    };
  }, [loadUserContext]);

  // Periodically check for token if we don't have data loaded
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token && !loading && tenants.length === 0 && !isSuperAdmin) {
      // Retry loading if we have a token but no tenants yet
      const timer = setTimeout(() => {
        loadUserContext();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [loading, tenants.length, isSuperAdmin, loadUserContext]);

  // Switch tenant
  const switchTenant = useCallback(async (tenant) => {
    if (!tenant?.id) return;
    
    try {
      // Header source for apiClient must flip before dependents refetch.
      localStorage.setItem('currentTenantId', tenant.id);
      setCurrentTenant(tenant);
      
      // Load permissions for this tenant
      const perms = await getMyPermissions(tenant.id);
      setPermissions(perms.permissions || []);
      setUserRole(perms.role);
      syncLocalUserRole(perms.role);
    } catch (err) {
      console.error('Failed to switch tenant:', err);
    }
  }, [syncLocalUserRole]);

  // Check if user has a specific permission
  const hasPermission = useCallback((permission) => {
    if (isSuperAdmin) return true;
    return permissions.includes(permission);
  }, [isSuperAdmin, permissions]);

  // Check if user has any of the given permissions
  const hasAnyPermission = useCallback((permissionList) => {
    if (isSuperAdmin) return true;
    return permissionList.some(p => permissions.includes(p));
  }, [isSuperAdmin, permissions]);

  // Check if a module is enabled for current tenant
  const isModuleEnabled = useCallback((module) => {
    // Super admin always has access to all modules
    if (isSuperAdmin) return true;
    
    // During loading, show all modules to prevent flicker
    if (loading) return true;
    
    // If no tenant selected, check if any tenant has the module
    if (!currentTenant) {
      return tenants.some(t => (t.enabled_modules || []).includes(module));
    }
    
    return (currentTenant.enabled_modules || []).includes(module);
  }, [isSuperAdmin, loading, currentTenant, tenants]);

  // Check if user can access a specific module
  const canAccessModule = useCallback((module) => {
    if (isSuperAdmin) return true;
    if (!currentTenant) return false;
    
    // Check if module is enabled for tenant's license
    if (!isModuleEnabled(module)) return false;
    
    // Check if user has at least read permission for the module
    const moduleReadPermissions = {
      grc: ['grc:policy:read', 'grc:risk:read', 'grc:control:read'],
      osint: ['osint:scan:read', 'osint:ioc:read'],
      litesabre: ['litesabre:scan:read', 'litesabre:vuln:read'],
      litesabre_pro: ['litesabre:scan:read', 'litesabre:vuln:read', 'litesabre_pro:scan:read'],
      amidala: ['amidala:scan:read', 'amidala:account:read'],
      sbom: ['litesabre:sbom:read']
    };
    
    const requiredPerms = moduleReadPermissions[module] || [];
    return requiredPerms.length === 0 || hasAnyPermission(requiredPerms);
  }, [isSuperAdmin, currentTenant, isModuleEnabled, hasAnyPermission]);

  // Check if user is tenant admin (Admin or Owner)
  const isTenantAdmin = useCallback(() => {
    if (isSuperAdmin) return true;
    return ['admin', 'owner'].includes(userRole);
  }, [isSuperAdmin, userRole]);

  // Refresh the context
  const refresh = useCallback(async () => {
    await loadUserContext();
  }, [loadUserContext]);

  const value = {
    loading,
    error,
    isSuperAdmin,
    tenants,
    currentTenant,
    permissions,
    userRole,
    
    // Actions
    switchTenant,
    refresh,
    
    // Permission helpers
    hasPermission,
    hasAnyPermission,
    isModuleEnabled,
    canAccessModule,
    isTenantAdmin
  };

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
};

export default TenantContext;
